import { RoseTree } from '@tableau/data-structures';
import { DestinationItem, NestedItemBasicInfo, NestedItemsTreeHelper } from '@tableau/nested-project-explorer';
import { useEffect, useState } from 'react';

import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { DestinationProjectMapper } from '~src/projects/models/mappers/DestinationProjectMapper';

import { SliceableHelper } from '../../httpClient/utils/SliceableHelper';
import { DestinationProjects } from '../../projects/resources/DestinationProjects';
import { getSessionManagementServiceInstance } from '../../sessionManagement/SessionManagementService';

export function useProjects() {
  const [projects, setProjects] = useState<RoseTree<NestedItemBasicInfo>[] | DestinationItem[]>(null);
  useEffect(() => {
    getAllProjects().then(projects => {
      setProjects(projects);
    });
  }, []); // No dependencies, so empty array to prevent re-renders
  return projects;
}

export function getSiteName(): string {
  return getSessionManagementServiceInstance().sessionSiteName();
}

export function getInitialProject(projects: RoseTree<NestedItemBasicInfo>[] | DestinationItem[], projectId: string) {
  if (!projects) {
    return null;
  }
  if (projectId) {
    return getProjectById(projects, projectId);
  } else {
    return getDefaultProject(projects);
  }
}

function getDefaultProject(projects: RoseTree<NestedItemBasicInfo>[] | DestinationItem[]): NestedItemBasicInfo | DestinationItem {
  if (isRoseTreeArray(projects)) {
    const lowestProject = projects.reduce((lowestProject: RoseTree<NestedItemBasicInfo>, project: RoseTree<NestedItemBasicInfo>) =>
      Number(lowestProject.node.id) > Number(project.node.id) && project.node.nestingLevel === 1 ? project : lowestProject
    , projects[0]);

    return lowestProject?.node;
  } else {
    let validProjectWithLowestId: DestinationItem | null = null;
    projects.forEach(project => {
      if (project.status === 'valid') {
        if (!validProjectWithLowestId || (validProjectWithLowestId && Number(project.id) < Number(validProjectWithLowestId.id))) {
          validProjectWithLowestId = project;
        }
      }
    });
    return validProjectWithLowestId;
  }
}

function getProjectById(projects: RoseTree<NestedItemBasicInfo>[] | DestinationItem[], projectId: string): NestedItemBasicInfo | DestinationItem {
  if (isRoseTreeArray(projects)) {
    let retProjectNode: NestedItemBasicInfo;
    const projNode = projects.find(projectNode => {
      if (projectNode.node.id === projectId) {
        return true;
      } else if (projectNode.node.hasChildren) {
        projectNode.forEach(p => {
          if (p.id === projectId) {
            retProjectNode = p;
          }});
        return false;
      }
    });
    return retProjectNode ? retProjectNode : projNode?.node ?? null;
  } else {
    return projects.find(project => project.id === projectId);
  }
}

export function isRoseTree(project: RoseTree<NestedItemBasicInfo> | DestinationItem): project is RoseTree<NestedItemBasicInfo> {
  return project instanceof RoseTree || ('node' in project && 'children' in project);
}

export function isRoseTreeArray(projects: RoseTree<NestedItemBasicInfo>[] | DestinationItem[]): projects is RoseTree<NestedItemBasicInfo>[] {
  return isRoseTree(projects[0]);
}

function getAllProjects(): Promise<RoseTree<NestedItemBasicInfo>[] | DestinationItem[]> {
  const contentType = 'project';
  const itemIds: string[] = [];
  const query = '';
  // Ideally this query could be replaced by the newer implementation in `ProjectTreeService`
  const projectsQuery = DestinationProjects.getQueryForSpecificSourceContentAndMatchText(contentType, itemIds, query);
  return SliceableHelper.fetchAll(projectsQuery).then(projects => {
    const destinationProjects = DestinationProjectMapper.map(projects.items);
    if (FeatureFlags.treeWidgetInNestedProjectExplorerEnabled) {
      return destinationProjects;
    } else {
      return NestedItemsTreeHelper.convertItemListToTrees(destinationProjects);
    }
  });
}
